/**
 * Offline BOL upload queue.
 *
 * Carriers uploading a signed BOL from a warehouse dock with weak signal can
 * hit "Submit" and have the file stashed in IndexedDB. When `window` regains
 * connectivity (`online` event) the queue is flushed automatically — each
 * queued upload is POSTed to /api/upload, then /api/my/loads/{id}/status is
 * PATCHed to `pod_submitted` with the resulting file_id.
 *
 * We intentionally do NOT use ServiceWorker Background Sync — iOS Safari has
 * zero support today, and the retry cadence is opaque. A foreground JS flush
 * driven by the `online` event works on every browser we care about.
 */
import { toast } from "@/lib/toast";

const DB_NAME = "mfg_offline_v1";
const STORE = "bol_queue";
const KEY_LISTENERS = "__mfg_offline_listeners__";

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(mode) {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueueBolUpload({ load_id, load_label, file }) {
  const buf = await file.arrayBuffer();
  const record = {
    id: `${load_id}-${Date.now()}`,
    load_id,
    load_label,
    filename: file.name,
    mime: file.type || "application/pdf",
    size: file.size,
    buffer: buf,
    queued_at: new Date().toISOString(),
    attempts: 0,
  };
  const store = await tx("readwrite");
  await new Promise((resolve, reject) => {
    const r = store.add(record);
    r.onsuccess = resolve;
    r.onerror = () => reject(r.error);
  });
  _notify();
  return record.id;
}

export async function listQueuedBolUploads() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function removeQueuedBolUpload(id) {
  const store = await tx("readwrite");
  await new Promise((resolve, reject) => {
    const r = store.delete(id);
    r.onsuccess = resolve;
    r.onerror = () => reject(r.error);
  });
  _notify();
}

async function updateQueuedBolUpload(id, patch) {
  const store = await tx("readwrite");
  const existing = await new Promise((resolve, reject) => {
    const r = store.get(id);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
  if (!existing) return;
  const store2 = await tx("readwrite");
  await new Promise((resolve, reject) => {
    const r = store2.put({ ...existing, ...patch });
    r.onsuccess = resolve;
    r.onerror = () => reject(r.error);
  });
}

/**
 * Flush the queue. Requires an axios/api instance so we hit the same
 * baseURL + Authorization headers as the rest of the app.
 */
export async function flushBolQueue(api) {
  if (!navigator.onLine) return { synced: 0, remaining: 0 };
  const queued = await listQueuedBolUploads();
  if (queued.length === 0) return { synced: 0, remaining: 0 };

  let synced = 0;
  for (const item of queued) {
    try {
      const fd = new FormData();
      const blob = new Blob([item.buffer], { type: item.mime });
      fd.append("file", blob, item.filename);
      fd.append("purpose", "pod");
      fd.append("doc_type", "signed_bol");
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const file_id = up.data.id;
      await api.post(`/my/loads/${item.load_id}/status`, {
        status: "pod_submitted",
        pod_file_id: file_id,
      });
      await removeQueuedBolUpload(item.id);
      synced += 1;
    } catch (e) {
      // Bump attempt counter; keep in queue for the next online window.
      await updateQueuedBolUpload(item.id, { attempts: (item.attempts || 0) + 1 });
    }
  }
  if (synced > 0) {
    toast.success(
      synced === 1
        ? "1 offline BOL synced to Dispatch ✓"
        : `${synced} offline BOLs synced to Dispatch ✓`,
      { duration: 6000 },
    );
  }
  const remaining = (await listQueuedBolUploads()).length;
  return { synced, remaining };
}

// -------- Listener registry so the header chip re-renders after mutations --------
function _getListeners() {
  if (!window[KEY_LISTENERS]) window[KEY_LISTENERS] = new Set();
  return window[KEY_LISTENERS];
}
function _notify() {
  _getListeners().forEach((fn) => { try { fn(); } catch (_) { /* ignore */ } });
}
export function subscribeBolQueue(fn) {
  _getListeners().add(fn);
  return () => _getListeners().delete(fn);
}

/**
 * Wire the auto-flush lifecycle exactly once per app load. Safe to call from
 * multiple mounts — the guard prevents duplicate listeners.
 */
let _wired = false;
export function installOfflineQueueLifecycle(api) {
  if (_wired) return;
  _wired = true;
  const attempt = () => flushBolQueue(api).catch(() => { /* ignore */ });
  window.addEventListener("online", attempt);
  // Best-effort flush shortly after mount in case we're already online but the
  // queue survived a page reload.
  setTimeout(attempt, 1500);
  // And every 60s while the tab is open — cheap belt-and-braces retry.
  setInterval(attempt, 60_000);
}
